import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { showError } from "../../utils/toastUtils";
import Cookies from "js-cookie";
import AuthAPI from "../../api/authApi.js";
import { useAppDispatch } from "../../store/hooks";
import { loginSuccess } from "../../store/slices/authSlice";

function Login() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const savedEmail = Cookies.get("rememberEmail");
    if (savedEmail) {
      setEmail(savedEmail);
      setRemember(true);
    }
    setPassword("");
  }, []);

  const validate = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) newErrors.email = "Email is required";
    else if (!emailRegex.test(email))
      newErrors.email = "Please enter a valid email";

    if (!password) newErrors.password = "Password is required";
    else if (password.length < 6)
      newErrors.password = "Minimum 6 characters required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    try {
      const response = await AuthAPI.login({
        email,
        password,
      });

      if (response.code !== 200 || !response.data) {
        setLoading(false);

        showError(response.message || "Login failed");

        return;
      }

      const { accessToken, refreshToken, user } = response.data;

      sessionStorage.setItem("accessToken", accessToken);

      sessionStorage.setItem("refreshToken", refreshToken);

      sessionStorage.setItem(
        "user",
        JSON.stringify({
          ...user,
          _id: user.id,
        }),
      );

      dispatch(
        loginSuccess({
          user: {
            ...user,
            _id: user.id,
          },
          token: accessToken,
        }),
      );

      if (remember) {
        Cookies.set("rememberEmail", email, { expires: 30 });
      } else {
        Cookies.remove("rememberEmail");
      }

      setPassword("");

      setLoading(false);

      navigate("/dashboard", {
        replace: true,
      });
    } catch (err) {
      setLoading(false);

      showError(err.message || "Login failed");
    }
  };

  return (
    <>
      <style>{`

.account-page{
background:
linear-gradient(
135deg,
#f8fafc,
#eef2ff,
#fff1f2
);

min-height:100vh;

overflow:hidden;

position:relative;
}

/* FLOATING BLOBS */

.account-page::before{
content:"";

position:absolute;

top:-120px;
left:-120px;

width:380px;
height:380px;

border-radius:50%;

background:
radial-gradient(
circle,
rgba(239,68,68,.12),
transparent 70%
);

animation:floatBlob 8s ease-in-out infinite;
}

.account-page::after{
content:"";

position:absolute;

bottom:-160px;
right:-120px;

width:420px;
height:420px;

border-radius:50%;

background:
radial-gradient(
circle,
rgba(99,102,241,.14),
transparent 70%
);

animation:floatBlob2 10s ease-in-out infinite;
}

.bg-backdrop{
position:relative;
z-index:2;
}

/* LOGIN CARD */

.login-glass-card{
position:relative;

padding:40px;

border-radius:32px;

background:
rgba(255,255,255,.72);

backdrop-filter:blur(18px);

border:
1px solid rgba(255,255,255,.7);

box-shadow:
0 20px 60px rgba(15,23,42,.08);

overflow:hidden;

animation:fadeUp .7s ease;
}

.login-glass-card::before{
content:"";

position:absolute;

top:0;
left:0;

width:100%;
height:4px;

background:
linear-gradient(
90deg,
#ef4444,
#f97316,
#8b5cf6
);
}

/* LOGO */

.logo img{
width:170px;

transition:.4s ease;
}

.logo img:hover{
transform:
scale(1.05)
rotate(-2deg);
}

/* TITLE */

.auth-title{
font-size:34px;
font-weight:800;

background:
linear-gradient(
90deg,
#111827,
#dc2626
);

-webkit-background-clip:text;
-webkit-text-fill-color:transparent;
}

.auth-subtitle{
font-size:15px;

color:#6b7280;

line-height:1.7;
}

/* LABEL */

.form-label{
font-size:14px;
font-weight:700;

color:#111827;

margin-bottom:10px;
}

/* INPUT */

.input-group{
position:relative;

border-radius:18px;

overflow:hidden;

transition:.35s ease;
}

.form-control{
height:58px !important;

border:none !important;

background:
rgba(248,250,252,.9) !important;

padding:0 18px !important;

font-size:15px !important;
font-weight:600;

transition:.35s ease !important;
}

.form-control:focus{
background:#fff !important;

box-shadow:
0 0 0 4px rgba(239,68,68,.12) !important;
}

.input-group-text{
background:
rgba(248,250,252,.9) !important;

border:none !important;

padding:0 18px;

transition:.35s ease;
}

.input-group:focus-within{
transform:translateY(-2px);

box-shadow:
0 10px 24px rgba(239,68,68,.08);
}

/* BUTTON */

.crazy-login-btn{
position:relative;

height:58px;

border:none !important;

border-radius:18px !important;

background:
linear-gradient(
135deg,
#dc2626,
#ef4444,
#f97316
) !important;

background-size:200% 200%;

font-size:16px !important;
font-weight:800 !important;

overflow:hidden;

transition:.4s ease !important;

animation:gradientMove 5s ease infinite;
}

.crazy-login-btn::before{
content:"";

position:absolute;

top:0;
left:-120%;

width:120%;
height:100%;

background:
linear-gradient(
90deg,
transparent,
rgba(255,255,255,.35),
transparent
);

transition:.8s ease;
}

.crazy-login-btn:hover{
transform:
translateY(-3px)
scale(1.01);

box-shadow:
0 18px 40px rgba(239,68,68,.28);
}

.crazy-login-btn:hover::before{
left:120%;
}

.crazy-login-btn:active{
transform:scale(.98);
}

/* LOADER */

.btn-loader{
width:22px;
height:22px;

border-radius:50%;

border:3px solid rgba(255,255,255,.35);
border-top-color:#fff;

animation:spin .7s linear infinite;
}

/* CHECKBOX */

.form-check-input{
width:20px;
height:20px;

cursor:pointer;

border-radius:6px !important;
}

.form-check-label{
font-weight:600;
}

/* COPYRIGHT */

.login-footer{
font-size:13px;

color:#9ca3af;
}

/* RIGHT IMAGE */

.account-bg-01{
position:relative;

background-position:center;
background-repeat:no-repeat;
background-size:contain;

animation:floatImage 5s ease-in-out infinite;
}

/* ANIMATIONS */

@keyframes fadeUp{
from{
opacity:0;
transform:
translateY(40px)
scale(.95);
}
to{
opacity:1;
transform:
translateY(0)
scale(1);
}
}

@keyframes gradientMove{
0%{
background-position:0% 50%;
}
50%{
background-position:100% 50%;
}
100%{
background-position:0% 50%;
}
}

@keyframes spin{
to{
transform:rotate(360deg);
}
}

@keyframes floatBlob{
0%,100%{
transform:translateY(0px);
}
50%{
transform:translateY(30px);
}
}

@keyframes floatBlob2{
0%,100%{
transform:translateY(0px);
}
50%{
transform:translateY(-35px);
}
}

@keyframes floatImage{
0%,100%{
transform:translateY(0px);
}
50%{
transform:translateY(-12px);
}
}

/* MOBILE */

@media(max-width:991px){

.login-glass-card{
padding:28px;
border-radius:24px;
}

.auth-title{
font-size:28px;
}

.logo img{
width:140px;
}
}

`}</style>
      <div className="account-page">
        {/* Main Wrapper */}
        <div className="main-wrapper">
          <div className="account-content">
            <div className="d-flex w-100 vh-100 overflow-hidden">
              <div className="col-lg-6 d-flex align-items-center justify-content-center vh-100 p-4 bg-backdrop">
                <form
                  method="post"
                  onSubmit={handleSubmit}
                  className="flex-fill"
                >
                  <div className="mx-auto mw-450 login-glass-card">
                    <div className="text-center mb-4 logo">
                      <img
                        src="assets/img/logo.png"
                        className="img-fluid"
                        alt="Logo"
                      />
                    </div>
                    <div className="mb-4">
                      <h4 className="mb-2 auth-title">Sign In</h4>
                      <p className="auth-subtitle">
                        Access the CRM Admin panel using your email and
                        passcode.
                      </p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">{"email"}</label>
                      <div className="input-group input-group-flat">
                        <input
                          type="email"
                          className="form-control"
                          autoComplete="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder={"email"}
                        />
                        <span className="input-group-text border-start-0">
                          <i className="ti ti-mail" />
                        </span>
                      </div>
                      {errors.email && (
                        <span className="text-danger">{errors.email}</span>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">{"password"}</label>
                      <div className="input-group input-group-flat pass-group">
                        <input
                          type={showPassword ? "text" : "password"}
                          placeholder={"password"}
                          className="form-control"
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          style={{ borderRight: "none" }}
                        />
                        <span
                          className="input-group-text bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{ cursor: "pointer" }}
                        >
                          <i
                            className={`ti ${showPassword ? "ti-eye" : "ti-eye-off"}`}
                            style={{
                              fontSize: "20px",
                              color: "#ef4444",
                              transition: ".3s ease",
                            }}
                          />
                        </span>
                      </div>
                      {errors.password && (
                        <span className="text-danger small">
                          {errors.password}
                        </span>
                      )}
                    </div>

                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="form-check form-check-md d-flex align-items-center">
                        <input
                          className="form-check-input mt-0"
                          type="checkbox"
                          checked={remember}
                          onChange={(e) => setRemember(e.target.checked)}
                        />
                        <label
                          className="form-check-label text-dark ms-1"
                          htmlFor="checkebox-md"
                        >
                          {"remember_me"}
                        </label>
                      </div>
                      <div className="text-end">
                        <Link
                          to="/admin/forgot-password"
                          className="link-danger fw-medium link-hover"
                        ></Link>
                      </div>
                    </div>
                    <div className="mb-3">
                      <button
                        type="submit"
                        disabled={loading}
                        className="btn w-100 crazy-login-btn"
                      >
                        {loading ? (
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            <div className="btn-loader"></div>
                            Signing In...
                          </div>
                        ) : (
                          <div className="d-flex align-items-center justify-content-center gap-2 text-white">
                            Login
                            <i className="ti ti-arrow-right"></i>
                          </div>
                        )}
                      </button>
                    </div>
                    <div className="text-center">
                      <p className="fw-medium login-footer">
                        Copyright © 2025 - Axepert Exhibits Pvt.Ltd.
                      </p>
                    </div>
                  </div>
                </form>
              </div>
              <div className="col-lg-6 d-none d-lg-flex align-items-center justify-content-center position-relative overflow-hidden">
                <div className="right-panel-content">
                  <div className="floating-circle circle-1"></div>
                  <div className="floating-circle circle-2"></div>
                  <div className="floating-circle circle-3"></div>

                  <div className="secure-card">
                    <img
                      src="/assets/img/login-security.jpg"
                      alt="Security"
                      className="secure-img"
                    />

                    <div className="feature-row">
                      <div className="feature-box">
                        <i className="ti ti-shield-lock"></i>
                        <div>
                          <h6>Secure Access</h6>
                          <p>Advanced protection</p>
                        </div>
                      </div>

                      <div className="feature-box">
                        <i className="ti ti-refresh"></i>
                        <div>
                          <h6>Realtime Sync</h6>
                          <p>Instant updates</p>
                        </div>
                      </div>

                      <div className="feature-box">
                        <i className="ti ti-chart-bar"></i>
                        <div>
                          <h6>Smart CRM</h6>
                          <p>Manage efficiently</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>{" "}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Login;
